import React, { useState } from 'react';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { SalesForm } from '../../components/SalesForm';
import { Building2, Home, Calendar, FileText, Users, CheckCircle, ArrowRight, Bot, Key, Search, MessageSquare } from 'lucide-react';
export function RealEstateSolutionPage() {
  const [isSalesFormOpen, setIsSalesFormOpen] = useState(false);
  return <div className="flex flex-col min-h-screen w-full" data-id="element-1359">
      <Header data-id="element-1360" />
      <main className="flex-grow" data-id="element-1361">
        {/* Hero Section */}
        <div className="bg-black text-white py-16 md:py-24" data-id="element-1362">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1363">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-1364">
              <div data-id="element-1365">
                <h1 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-1366">
                  AI Solutions for Real Estate
                </h1>
                <p className="text-xl text-gray-300 mb-8" data-id="element-1367">
                  Transform your real estate business with intelligent AI agents
                  that automate listings, streamline client communications, and
                  accelerate property transactions.
                </p>
                <button onClick={() => setIsSalesFormOpen(true)} className="bg-white text-black px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors" data-id="element-1368">
                  Request a Demo
                </button>
              </div>
              <div className="relative" data-id="element-1369">
                <img src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Modern real estate property" className="rounded-lg shadow-lg" data-id="element-1370" />
                <div className="absolute -bottom-6 -right-6 bg-white p-4 rounded-lg shadow-lg" data-id="element-1371">
                  <Building2 className="text-black" size={32} data-id="element-1372" />
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Key Benefits */}
        <div className="py-16 md:py-24 bg-white" data-id="element-1373">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1374">
            <div className="text-center mb-16" data-id="element-1375">
              <h2 className="text-3xl font-bold mb-4" data-id="element-1376">
                Key Benefits for Real Estate Businesses
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1377">
                Our AI agents address the unique challenges faced by real estate
                agencies, property managers, and brokerages.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1378">
              <BenefitCard icon={<Home size={24} data-id="element-1380" />} title="Automated Listings Management" description="AI agents that create, update, and syndicate property listings across multiple platforms with consistent information." data-id="element-1379" />
              <BenefitCard icon={<Calendar size={24} data-id="element-1382" />} title="Intelligent Scheduling" description="Automate property viewings, client meetings, and follow-ups with smart scheduling that adapts to your availability." data-id="element-1381" />
              <BenefitCard icon={<FileText size={24} data-id="element-1384" />} title="Document Processing" description="Streamline contracts, disclosures, and other paperwork with automated document generation and management." data-id="element-1383" />
              <BenefitCard icon={<Users size={24} data-id="element-1386" />} title="Client Relationship Management" description="Maintain consistent communication with buyers, sellers, and leads through personalized, automated outreach." data-id="element-1385" />
              <BenefitCard icon={<Key size={24} data-id="element-1388" />} title="Property Management" description="Simplify tenant communication, maintenance requests, and rent collection with intelligent automation." data-id="element-1387" />
              <BenefitCard icon={<Bot size={24} data-id="element-1390" />} title="Market Intelligence" description="Gain insights from property data, market trends, and competitor analysis to make informed decisions." data-id="element-1389" />
            </div>
          </div>
        </div>
        {/* Features */}
        <div className="py-16 md:py-24 bg-white" data-id="element-1391">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1392">
            <div className="text-center mb-16" data-id="element-1393">
              <h2 className="text-3xl font-bold mb-4" data-id="element-1394">
                Key Features for Real Estate Professionals
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1395">
                Our AI agents offer specialized capabilities designed for the
                unique needs of the real estate industry.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1396">
              <FeatureCard title="24/7 Lead Response" description="AI agents that instantly respond to property inquiries from websites, portals, and social media at any time of day or night." data-id="element-1397" />
              <FeatureCard title="Automated Property Descriptions" description="Generate compelling, accurate property descriptions and marketing materials based on property details and photos." data-id="element-1398" />
              <FeatureCard title="Smart Lead Qualification" description="Qualify and prioritize leads based on buying signals, budget alignment, and timeline to focus on the most promising opportunities." data-id="element-1399" />
              <FeatureCard title="Transaction Coordination" description="Automate the coordination of inspections, appraisals, and closing activities with all parties involved in a transaction." data-id="element-1400" />
              <FeatureCard title="Competitive Market Analysis" description="Generate detailed CMA reports with AI-powered analysis of comparable properties, market trends, and pricing recommendations." data-id="element-1401" />
              <FeatureCard title="Post-Transaction Follow-up" description="Maintain relationships with past clients through automated check-ins, anniversary messages, and market updates." data-id="element-1402" />
            </div>
          </div>
        </div>
        {/* Agent Ideas Showcase */}
        <div className="py-16 md:py-24 bg-gray-50" data-id="element-1403">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1404">
            <div className="text-center mb-16" data-id="element-1405">
              <h2 className="text-3xl font-bold mb-4" data-id="element-1406">
                AI Agents for Real Estate
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1407">
                Discover specialized AI agents designed specifically for real
                estate businesses.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1408">
              <AgentCard icon={<Search size={24} data-id="element-1410" />} title="Listing Optimization Agent" description="Enhances property listings to maximize visibility and appeal across multiple platforms." capabilities={['Analyzes high-performing listings to identify effective keywords and descriptions', 'Optimizes listing titles and descriptions for better search visibility', 'Recommends ideal photo order and highlights based on buyer preferences', 'Ensures consistent property information across all listing platforms', 'Schedules listing refreshes to maintain visibility in search results', 'Provides performance analytics to continually improve listing effectiveness']} data-id="element-1409" />
              <AgentCard icon={<MessageSquare size={24} data-id="element-1412" />} title="Client Communication Agent" description="Maintains consistent, personalized communication with clients throughout their real estate journey." capabilities={['Sends timely updates on new listings matching buyer criteria', 'Provides automatic responses to common property inquiries', 'Schedules and confirms property viewings with interested parties', 'Follows up after showings to gather feedback and maintain engagement', 'Sends transaction milestone updates to all stakeholders', 'Maintains long-term relationships with past clients through periodic check-ins']} data-id="element-1411" />
              <AgentCard icon={<FileText size={24} data-id="element-1414" />} title="Documentation Assistant" description="Streamlines the creation, management, and processing of real estate documentation." capabilities={['Pre-fills contracts and disclosure forms with property and client information', 'Tracks document status and sends reminders for outstanding signatures', 'Verifies document completeness and flags potential issues or missing information', 'Organizes and archives transaction documents in a searchable database', 'Generates custom reports for clients, lenders, and other stakeholders', 'Ensures compliance with local real estate regulations and requirements']} data-id="element-1413" />
              <AgentCard icon={<Key size={24} data-id="element-1416" />} title="Property Management Agent" description="Automates and optimizes the day-to-day operations of rental property management." capabilities={['Processes rental applications and conducts initial tenant screening', 'Manages maintenance requests from tenants and coordinates with service providers', 'Automates rent collection and payment reminders', 'Conducts regular property check-ins and generates inspection reports', 'Handles lease renewals and rent adjustment communications', 'Provides owners with regular performance reports and occupancy updates']} data-id="element-1415" />
            </div>
          </div>
        </div>
        {/* How It Works */}
        <div className="py-16 md:py-24 bg-gray-50" data-id="element-1417">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1418">
            <div className="text-center mb-16" data-id="element-1419">
              <h2 className="text-3xl font-bold mb-4" data-id="element-1420">How It Works</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1421">
                Implementing Agentter.ai in your real estate business is
                straightforward and delivers rapid results.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1422">
              <StepCard number="1" title="Connect Your Systems" description="Integrate with your existing CRM, MLS, property management, and marketing platforms with our no-code connectors." data-id="element-1423" />
              <StepCard number="2" title="Configure Your Agents" description="Define the tasks you want to automate using natural language - no coding required." data-id="element-1424" />
              <StepCard number="3" title="Deploy and Optimize" description="Launch your AI agents and continuously refine their performance based on results and feedback." data-id="element-1425" />
            </div>
          </div>
        </div>
        {/* Integrations */}
        <div className="py-16 md:py-24 bg-white" data-id="element-1426">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1427">
            <div className="text-center mb-16" data-id="element-1428">
              <h2 className="text-3xl font-bold mb-4" data-id="element-1429">Seamless Integrations</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1430">
                Our platform connects with the tools and software you already
                use in your real estate operations.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-1431">
              {['MLS Systems', 'Zillow', 'Realtor.com', 'Redfin', 'Salesforce', 'HubSpot', 'DocuSign', 'Dotloop', 'Buildium', 'AppFolio', 'QuickBooks', 'Mailchimp'].map((integration, index) => <div key={index} className="bg-gray-50 p-4 rounded-lg text-center" data-id="element-1432">
                  <span className="font-medium" data-id="element-1433">{integration}</span>
                </div>)}
            </div>
          </div>
        </div>
        {/* CTA Section */}
        <div className="bg-black text-white py-16" data-id="element-1434">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1435">
            <h2 className="text-3xl font-bold mb-6" data-id="element-1436">
              Ready to transform your real estate business?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto" data-id="element-1437">
              Schedule a personalized demo to see how Agentter.ai can automate
              and optimize your real estate workflows.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4" data-id="element-1438">
              <button onClick={() => setIsSalesFormOpen(true)} className="bg-white text-black px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors" data-id="element-1439">
                Request a Demo
              </button>
              <a href="/pricing" className="border border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-900 transition-colors" data-id="element-1440">
                View Pricing
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-1441" />
      {/* Sales Form Modal */}
      <SalesForm isOpen={isSalesFormOpen} onClose={() => setIsSalesFormOpen(false)} agentType="Real Estate Solutions" data-id="element-1442" />
    </div>;
}
function BenefitCard({
  icon,
  title,
  description
}) {
  return <div className="bg-gray-50 p-6 rounded-lg border border-gray-100 hover:shadow-md transition-shadow" data-id="element-1443">
      <div className="bg-black text-white p-3 rounded-lg inline-block mb-4" data-id="element-1444">
        {icon}
      </div>
      <h3 className="text-xl font-semibold mb-2" data-id="element-1445">{title}</h3>
      <p className="text-gray-600" data-id="element-1446">{description}</p>
    </div>;
}
function AgentCard({
  icon,
  title,
  description,
  capabilities
}) {
  return <div className="bg-white p-6 rounded-lg border border-gray-100 hover:shadow-md transition-shadow" data-id="element-1447">
      <div className="flex items-center mb-4" data-id="element-1448">
        <div className="bg-black text-white p-3 rounded-lg mr-4" data-id="element-1449">{icon}</div>
        <h3 className="text-xl font-semibold" data-id="element-1450">{title}</h3>
      </div>
      <p className="text-gray-600 mb-4" data-id="element-1451">{description}</p>
      <div data-id="element-1452">
        <h4 className="font-medium text-sm text-gray-500 mb-2" data-id="element-1453">
          CAPABILITIES:
        </h4>
        <ul className="space-y-2" data-id="element-1454">
          {capabilities.map((capability, index) => <li key={index} className="flex items-start" data-id="element-1455">
              <CheckCircle className="text-green-500 mr-2 mt-0.5 flex-shrink-0" size={16} data-id="element-1456" />
              <span className="text-sm" data-id="element-1457">{capability}</span>
            </li>)}
        </ul>
      </div>
    </div>;
}
function StepCard({
  number,
  title,
  description
}) {
  return <div className="bg-white p-6 rounded-lg border border-gray-100 hover:shadow-md transition-shadow" data-id="element-1458">
      <div className="bg-black text-white w-10 h-10 rounded-full flex items-center justify-center mb-4 font-bold" data-id="element-1459">
        {number}
      </div>
      <h3 className="text-xl font-semibold mb-2" data-id="element-1460">{title}</h3>
      <p className="text-gray-600" data-id="element-1461">{description}</p>
    </div>;
}
function FeatureCard({
  title,
  description
}) {
  return <div className="bg-white p-6 rounded-lg border border-gray-100 hover:shadow-md transition-shadow" data-id="element-1462">
      <h3 className="text-xl font-semibold mb-3" data-id="element-1463">{title}</h3>
      <p className="text-gray-600" data-id="element-1464">{description}</p>
    </div>;
}